import React from 'lib/react';
import { useTranslation } from 'lib/translation';
import { useSelector } from 'lib/redux';
import useMediaQuery from 'hooks/useMediaQuery';
import PropTypes from 'prop-types';
import { makeStyles } from 'lib/components';
import {
  isOrderingDisabledSelector,
  isEntitledForHidePricingSelector,
  featureFlagSelector,
  isSubscriptionDashboardV2EnabledSelector,
} from 'utils/appSelectors.utils';
import { ENABLE_GEOMNI_ESX_ADDON_LOGIC, ESX_FOR_UPGRADE } from 'constants/FeatureFlag.constants';
import OrderHistoryStyles from '../../features/orderHistory/OrderHistoryStyles';
import ReportsTable from './ReportsTable.component';
import ReportsList from './ReportsList.component';
import ReportsTableSubscription from './ReportsTableSubscription.component';
import ReportsListSubscription from './ReportsListSubscription.component';
import {
  getOrderHistoryCells, getOrderHistoryColumns, getOrderHistoryLoadingCells,
  getSubscriptionOrderHistoryCells,
  getSubscriptionOrderHistoryLoadingCells,
} from '../../features/orderHistory/OrderHistory.utils';

const fieldsStyles = makeStyles(() => ({
  costCell: {
    textAlign: 'right',
  },
  actionsCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
}));

function OrdersList({
  getFields, propertyDataMap = {}, ...rest
}) {
  const {
    loading, availableActions, reportDetails, searchText, isBillingAffiliateUser, subscriptionDetailsMap,
  } = useSelector(({
    orderHistory, userData: { initParameters },
  }) => ({
    ...orderHistory,
    // #genai: use optional chaining (Sonar S6582); initParameters is either an object or null/undefined, so semantics are unchanged
    isBillingAffiliateUser: initParameters?.isBillingAffiliate,
  }));

  const isEntitledForHidePricing = useSelector(isEntitledForHidePricingSelector);
  const isOrderingDisabled = useSelector(isOrderingDisabledSelector);
  const isESXEnabled = useSelector(featureFlagSelector(ENABLE_GEOMNI_ESX_ADDON_LOGIC));
  const esxForUpgradeFlag = useSelector(featureFlagSelector(ESX_FOR_UPGRADE)); // #genai
  const isSubscriptionDashboardV2Enabled = useSelector(isSubscriptionDashboardV2EnabledSelector);
  const isOrderHistoryEsxFilesUpgradeEnabled = Boolean(esxForUpgradeFlag && !isSubscriptionDashboardV2Enabled);

  const { t } = useTranslation();

  const isMobile = useMediaQuery(640);
  const isTablet = useMediaQuery(1200);
  const orderHistoryClasses = OrderHistoryStyles();
  const fieldsClasses = fieldsStyles();

  const useCompactLayout = isMobile || isTablet;
  let ReportsComponent = ReportsTable;
  if (isSubscriptionDashboardV2Enabled) {
    ReportsComponent = useCompactLayout ? ReportsListSubscription : ReportsTableSubscription;
  } else if (useCompactLayout) {
    ReportsComponent = ReportsList;
  }

  const loadingCellsArray = new Array(useCompactLayout ? 5 : 10).fill({});

  const fields = !isSubscriptionDashboardV2Enabled && getFields(t, fieldsClasses);

  let reportDetailsList;
  if (loading && isSubscriptionDashboardV2Enabled) {
    reportDetailsList = getSubscriptionOrderHistoryLoadingCells(loadingCellsArray, useCompactLayout, orderHistoryClasses);
  } else if (loading) {
    reportDetailsList = getOrderHistoryLoadingCells(loadingCellsArray, useCompactLayout, orderHistoryClasses);
  } else if (isSubscriptionDashboardV2Enabled) {
    reportDetailsList = getSubscriptionOrderHistoryCells(
      reportDetails || [],
      t,
      orderHistoryClasses,
      availableActions,
      isOrderingDisabled,
      subscriptionDetailsMap || {},
      isBillingAffiliateUser,
      isEntitledForHidePricing,
      searchText,
      false,
      propertyDataMap,
      isESXEnabled,
      isOrderHistoryEsxFilesUpgradeEnabled,
    );
  } else {
    reportDetailsList = getOrderHistoryCells(
      reportDetails || [],
      t,
      orderHistoryClasses,
      availableActions,
      isOrderingDisabled,
      isBillingAffiliateUser,
      isEntitledForHidePricing,
      searchText,
      false,
      propertyDataMap,
      isESXEnabled,
      isOrderHistoryEsxFilesUpgradeEnabled,
    );
  }

  return (
    <ReportsComponent
      fields={fields}
      reportDetailsList={reportDetailsList}
      loading={loading}
      {...rest}
    />
  );
}

OrdersList.defaultProps = {
  getFields: getOrderHistoryColumns,
  propertyDataMap: {},
};

OrdersList.propTypes = {
  getFields: PropTypes.func,
  propertyDataMap: PropTypes.objectOf(
    PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ),
};

export default OrdersList;
